import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { secure } from 'utils/client';
import { LIST, tags, types } from '../consts';

const { REACT_APP_API_URL: baseUrl } = process.env;

const labsAPI = createApi({
  reducerPath: 'labs',
  tagTypes: Object.values(types),
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (build) => ({
    getListCustomerGroups: build.query({
      query: () =>
        secure({
          method: 'GET',
          url: '/lab-groups',
        }),
      providesTags: [tags.LAB_GROUPS(LIST)],
    }),

    // [POST] /lab-groups
    createCustomerGroup: build.mutation({
      query: (data) =>
        secure({
          method: 'POST',
          url: '/lab-groups',
          body: data,
        }),
      invalidatesTags: [tags.LAB_GROUPS(LIST)],
    }),

    deleteCustomerGroup: build.mutation({
      query: (group_id) =>
        secure({
          method: 'DELETE',
          url: `/lab-groups/${group_id}`,
        }),
      invalidatesTags: [tags.LAB_GROUPS(LIST)],
    }),

    mergeCustomerGroups: build.mutation({
      query: ({ group_from, group_to, body }) =>
        secure({
          method: 'POST',
          url: `/lab-groups/merge/${group_from}/${group_to}`,
          body,
        }),
      invalidatesTags: [tags.LAB_GROUPS(LIST)],
    }),

    // [GET] /lab-groups/${group_id}/roles
    getCustomerGroupSettings: build.query({
      query: ({ group_id }) =>
        secure({
          method: 'GET',
          url: `/lab-groups/${group_id}/roles`,
        }),
      providesTags: (result, error, { group_id }) => [
        tags.LAB_GROUP_SETTINGS(group_id),
      ],
    }),

    patchCustomerGroupSettings: build.mutation({
      query: ({ group_id, body }) =>
        secure({
          method: 'PUT',
          url: `/lab-groups/${group_id}/roles`,
          body,
        }),
      invalidatesTags: [tags.LAB_GROUP_SETTINGS(LIST)],
    }),

    patchCustomerGroup: build.mutation({
      query: ({ group_id, body }) =>
        secure({
          method: 'PATCH',
          url: `/lab-groups/${group_id}/update`,
          body,
        }),
      invalidatesTags: [tags.LAB_GROUPS(LIST)],
    }),

    // [GET] /lab-groups/{group_id}/members
    getGroupMembers: build.query({
      query: ({ group_id }) =>
        secure({
          method: 'GET',
          url: `/lab-groups/${group_id}/members`,
        }),
      providesTags: (result, error, { group_id }) => [
        tags.LAB_GROUP_MEMBERS(group_id),
      ],
    }),

    // [POST] /lab-groups/{group_id}/members
    createCustomerGroupInvite: build.mutation({
      query: ({ group_id, body }) =>
        secure({
          method: 'POST',
          url: `/lab-groups/${group_id}/invite`,
          body,
        }),
      invalidatesTags: (result, error, { group_id }) => [
        tags.LAB_GROUP_MEMBERS(group_id),
      ],
    }),

    // [POST] /lab-groups/{group_id}/members
    deleteCustomerGroupMember: build.mutation({
      query: ({ group_id, customer_id }) =>
        secure({
          method: 'DELETE',
          url: `/lab-groups/${group_id}/members/${customer_id}`,
        }),
      invalidatesTags: (result, error, { group_id }) => [
        tags.LAB_GROUP_MEMBERS(group_id),
      ],
    }),

    // [GET] /lab-groups/invite/accept/${invitation_hash}
    acceptCustomerGroupInvitation: build.mutation({
      query: (invitation_hash) =>
        secure({
          method: 'GET',
          url: `/lab-groups/invite/accept/${invitation_hash}`,
        }),
      invalidatesTags: (result, error, { group_id }) => [
        tags.LAB_GROUP_MEMBERS(group_id),
      ],
    }),

    // [GET] /lab-groups/invite/accept/${invitation_hash}
    cancelCustomerGroupInvitation: build.mutation({
      query: (invitation_hash) =>
        secure({
          method: 'GET',
          url: `/lab-groups/invite/cancel/${invitation_hash}`,
        }),
      invalidatesTags: (result, error, { group_id }) => [
        tags.LAB_GROUP_MEMBERS(group_id),
      ],
    }),

    // [DELETE] lab-groups/${data.group_id}/invites/${data.invite_id}
    rejectCustomerGroupInvite: build.mutation({
      query: ({ group_id, invite_id }) =>
        secure({
          method: 'DELETE',
          url: `/lab-groups/${group_id}/invites/${invite_id}`,
        }),
      invalidatesTags: (result, error, { group_id }) => [
        tags.LAB_GROUP_MEMBERS(group_id),
      ],
    }),
  }),
});

export default labsAPI;
